import PropTypes from 'prop-types';
import useResizeObserver from 'use-resize-observer';
import React, { useContext, useRef, useState } from 'react';
import { Box, Column, Optional, Row } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';

import AddressBar from '../MobileLayout/AddressBar';
import DealerAccordion from '../DealerAccordion';
import DealerCardsList from '../DealerCardsList';
import DealerContactQuickLinks from '../DealerContactQuickLinks/DealerContactQuickLinks';
import DealerDetailsModal from '../DealerDetailsModal';
import DealerLocatorInput from '../DealerLocatorInput';
import DealerLocatorInputOptions from '../DealerLocatorInputOptions';
import DepartmentTabs from '../DepartmentTabs';
import EmbeddedGoogleMap from '../EmbeddedGoogleMap/index';
import formatAddress from '../../utils/formatAddress';
import themeStyles from './DesktopLayout.style';
import useConfiguration from '../../hooks/useConfiguration';
import usePlaceFromCookie from '../../hooks/usePlaceFromCookie';
import useRedirectToLeadClient from '../../hooks/useRedirectToLeadClient';
import { DealersListContext } from '../DealersListContext';

const Container = themeStyles.apply(Row, 'Container');
const LeftColumn = themeStyles.apply(Column, 'LeftColumn');
const RightColumn = themeStyles.apply(Column, 'RightColumn');

const DesktopLayout = ({ variant }) => {
  const dealerCardsListRef = useRef(null);
  const [isDealerCardScrolled, setIsDealerCardScrolled] = useState(false);

  const { t } = useTranslation();
  const {
    selectedDealer,
    isOpen,
    selectedPlace,
    setIsOpen,
    setSelectedPlace,
    inputValue,
    setInputValue,
    setSelectedDealer,
  } = useContext(DealersListContext);
  const { showQuickLinks } = useConfiguration();
  const { ref, height } = useResizeObserver();

  const address = formatAddress(selectedPlace);
  const buttonLabel = selectedPlace
    ? t('Sub-apps.Dealers.locationEditButton')
    : t('Sub-apps.Dealers.locationAddButton');
  const locationLabel = selectedPlace ? address : t('Sub-apps.Dealers.enterLocationLabel');
  useRedirectToLeadClient();
  usePlaceFromCookie();

  const DEFAULT_NAV_HEIGHT = '85px';

  return (
    <Container data-testid="cy-dealer-locator">
      <LeftColumn>
        <Box pl="big" pr="default" ref={ref}>
          <DealerLocatorInputOptions />
          {isDealerCardScrolled && !isOpen ? (
            <AddressBar
              dealerCardsListRef={dealerCardsListRef}
              locationLabel={locationLabel}
              buttonLabel={buttonLabel}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              setSelectedDealer={setSelectedDealer}
              setSelectedPlace={setSelectedPlace}
              inputValue={inputValue}
              setInputValue={setInputValue}
            />
          ) : (
            <DealerLocatorInput
              setIsOpen={setIsOpen}
              setSelectedPlace={setSelectedPlace}
              inputValue={inputValue}
              setInputValue={setInputValue}
              setSelectedDealer={setSelectedDealer}
            />
          )}
        </Box>
        <Box style={{ flex: 1 }}>
          <DealerCardsList
            dealerCardsListRef={dealerCardsListRef}
            inputHeight={height}
            setIsDealerCardScrolled={setIsDealerCardScrolled}
          />
          <DealerDetailsModal dealer={selectedDealer}>
            <DepartmentTabs dealer={selectedDealer} variant={variant} />
            <Optional when={!showQuickLinks}>
              <DealerAccordion productLines={selectedDealer?.productLines} />
            </Optional>
            <Optional when={showQuickLinks}>
              <DealerContactQuickLinks
                dealerCode={selectedDealer?.code}
                serviceBookingUrl={selectedDealer?.serviceBookingUrl}
              />
            </Optional>
          </DealerDetailsModal>
        </Box>
      </LeftColumn>
      <RightColumn>
        <EmbeddedGoogleMap width="100%" height={`calc(100vh - ${DEFAULT_NAV_HEIGHT})`} />
      </RightColumn>
    </Container>
  );
};

DesktopLayout.propTypes = {
  variant: PropTypes.string,
};

export default DesktopLayout;
